import { AgGridReact } from "@ag-grid-community/react";
import { Box, Flex, HStack, Skeleton, Stack } from "@chakra-ui/react";
import React from "react";
import CoreDataGridContainer from "../../../components/datagrid/CoreDataGridContainer";
import { QuickNavigator } from "../../../components/datagrid/QuickNavigator";
import RegistryFilterMode from "../../../components/datagrid/RegistryFilterMode";
import RegistryLeaf from "../../../components/datagrid/RegistryLeaf";
import RegistryPane from "../../../components/datagrid/RegistryPane";
import RegistryToolbar from "../../../components/datagrid/RegistryToolbar";
import useReadCohort from "../../../services/api/cohorts/queries/useReadCohort";
import { useReadCohortData } from "../../../services/api/data/queries/useReadCohortData";
import { useSetHomeState } from "../../registries/stores/actions/useSetHomeState";
import { useSelectIsCaseView } from "../../registries/stores/select/useSelectIsCaseView";
import { useSelectIsPaneOpen } from "../../registries/stores/select/useSelectIsPaneOpen";

interface IProps {
  cohortId: string;
}

const CohortDataGrid = ({ cohortId }: IProps) => {
  const gridRef: any = React.useRef<AgGridReact>(); // Optional - for accessing Grid's API

  const [isLeafOpen, set_isLeafOpen] = React.useState(false);
  const [isGridReady, setIsGridReady] = React.useState(false);

  const isPaneOpen = useSelectIsPaneOpen();
  const isCaseView = useSelectIsCaseView();

  const setHomeState = useSetHomeState();

  let {
    data: dataCohort,
    isError: isErrorCohortData,
    isLoading: isLoadingCohortData,
    isRefetching: isRefetchingCohortData,
    error: errorCohortData,
  } = useReadCohortData({
    cohortId,
  });

  let { data: cohort } = useReadCohort(cohortId);

  const handleOnGridReady = () => {
    setIsGridReady(true);
  };

  React.useEffect(() => {
    const isLeadOpenUpdate = isCaseView;
    set_isLeafOpen(!!isLeadOpenUpdate);
  }, [isCaseView]);

  React.useEffect(() => {
    setHomeState();
    setIsGridReady(false);
  }, [cohortId, setHomeState]);

  React.useEffect(() => {
    if (!gridRef || !gridRef.current || !gridRef.current.api) {
      setIsGridReady(false);
    }
  }, [gridRef]);

  React.useEffect(() => {
    // If data is loading, grid is not ready
    if (isLoadingCohortData || isRefetchingCohortData) {
      setIsGridReady(false);
    }
  }, [isLoadingCohortData, isRefetchingCohortData]);

  return (
    <Stack w="full" h="full" spacing={0} overflow="hidden">
      {/* tabs and toolbar */}
      <HStack justifyContent={"space-between"} flexWrap={"wrap"} py="4">
        <QuickNavigator
          zIndex="2"
          gridRef={gridRef}
          cohort={cohort}
          isGridReady={isGridReady}
          px={"4"}
        ></QuickNavigator>
        {!isErrorCohortData && (
          <>
            {gridRef && gridRef.current && gridRef.current.api ? (
              <RegistryToolbar
                gridRef={gridRef}
                isAllowEdit={false}
                isAllowAdd={false}
                isAllowGeneMap={false}
              ></RegistryToolbar>
            ) : (
              <HStack spacing={6} px={"4"}>
                <Skeleton h="8" w="8"></Skeleton>
                <Skeleton h="8" w="8"></Skeleton>
                <Skeleton h="8" w="8"></Skeleton>
                <Skeleton h="8" w="32"></Skeleton>
              </HStack>
            )}
          </>
        )}
      </HStack>

      {isCaseView && (
        <Box>
          <RegistryFilterMode
            gridRef={gridRef}
            isGridReady={isGridReady}
          ></RegistryFilterMode>
        </Box>
      )}

      {/* data canvas */}
      <Stack
        minH={"1px"}
        flex="1"
        position={"relative"}
        overflow={isLeafOpen ? "auto" : "hidden"}
        spacing={0}
        borderTop={"1px"}
        borderColor="border"
      >
        <Flex
          marginTop={isLeafOpen ? "0" : "-500px"}
          w="full"
          h={"500px"}
          minH={"500px"}
          transition={"all .2s ease-in"}
        >
          <RegistryLeaf gridRef={gridRef}></RegistryLeaf>
        </Flex>

        <Box
          position="relative"
          overflow={isLeafOpen ? "unset" : "hidden"}
          // Add to allow table to fill more space
          minH={isLeafOpen ? "50vh" : "full"}
          // h="full"
        >
          <Flex
            h="full"
            minW={isPaneOpen ? "calc(100% - 700px)" : "100%"}
            w={isPaneOpen ? "45%" : "100%"}
            transition={"all .2s ease-in"}
            position="relative"
          >
            <CoreDataGridContainer
              gridRef={gridRef}
              data={dataCohort}
              isLoading={isLoadingCohortData || isRefetchingCohortData}
              errorCode={errorCohortData?.response?.status}
              errorTitle={
                errorCohortData?.response?.status == 404
                  ? "Cohort not found"
                  : "Error loading cohort"
              }
              errorDescription={
                errorCohortData?.response?.status == 404
                  ? "We looked everywhere, but it seems this cohort has been deleted."
                  : "So sorry about this! Please refresh or message us if you keep seeing this error."
              }
              isError={isErrorCohortData}
              setIsGridReady={handleOnGridReady}
              isCohort={true}
              cohortId={cohortId}
            ></CoreDataGridContainer>
          </Flex>

          <Flex
            top="0"
            position="absolute"
            transform={isPaneOpen ? "translateX(-100%)" : "translateX(0%)"}
            // Need to make it not extend on overlfow x when not open but leaf is
            w={!isPaneOpen && isLeafOpen ? "0" : "55%"}
            left="100%"
            bottom="0"
            bg="bg.main"
            transition={"all .2s ease-in"}
            maxW="700px"
            shadow="lg"
            overflow="hidden"
          >
            <RegistryPane
              isGridReady={isGridReady}
              gridRef={gridRef}
            ></RegistryPane>
          </Flex>
        </Box>
      </Stack>
    </Stack>
  );
};

export default CohortDataGrid;
